import { useState, useEffect, useCallback } from 'react';

import { ContentDiscoveryService } from './contentDiscovery';
import { TrackingV2Service } from './trackingService';

/**
 * Hook to get chapter tracking data
 */
export const useChapterTracking = (userId, level, chapter, _options = {}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!userId || !level || !chapter) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const chapterData = await TrackingV2Service.getChapterData(userId, level, chapter);
            setData(chapterData);
            setError(null);
        } catch (err) {
            setError(err);
            console.error('Error loading chapter tracking:', err);
        } finally {
            setLoading(false);
        }
    }, [userId, level, chapter]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { data, loading, error, refresh };
};

/**
 * Hook to get learning progress data
 */
export const useLearningProgress = (userId, level, chapter, options = {}) => {
    const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

    return {
        data: data?.learningProgress || null,
        loading,
        error,
        refresh
    };
};

/**
 * Hook to get mastery data
 */
export const useMasteryData = (userId, level, chapter, options = {}) => {
    const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

    return {
        data: data?.masteryData || null,
        loading,
        error,
        refresh
    };
};

/**
 * Hook to get content discovery data
 */
export const useContentDiscovery = (level, chapter, institution = 'Korea_University', _options = {}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!level || !chapter) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const contentData = await ContentDiscoveryService.getContentMetadata(level, chapter, institution);
            setData(contentData);
            setError(null);
        } catch (err) {
            setError(err);
            console.error('Error loading content discovery:', err);
        } finally {
            setLoading(false);
        }
    }, [level, chapter, institution]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { data, loading, error, refresh };
};

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/**
 * Legacy compatibility hook
 */
export const useKnowledgeProfile = (userId, level, chapter, _sessionType = 'Learn', _options = {}) => {
    const { data: masteryData, loading, error } = useMasteryData(userId, level, chapter, _options);
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        if (!masteryData) return;

        // Build profile from mastery data
        const profile = {
            vocabulary: {
                exposed_only: [],        // Seen but never tested
                has_incorrect: [],       // Has at least one wrong attempt
                all_correct: []          // All attempts correct (understood)
            },
            grammar: {
                exposed_only: [],
                has_incorrect: [],
                all_correct: []
            }
        };

        // Process vocabulary mastery
        Object.entries(masteryData.vocabulary || {}).forEach(([wordId, mastery]) => {
            if (mastery.isExposed) {
                const hasIncorrect = Object.values(mastery.learning || {}).some(attempt => !attempt.isCorrect);
                const hasCorrect = Object.values(mastery.learning || {}).some(attempt => attempt.isCorrect);

                if (hasIncorrect) {
                    profile.vocabulary.has_incorrect.push(wordId);
                } else if (hasCorrect) {
                    profile.vocabulary.all_correct.push(wordId);
                } else {
                    profile.vocabulary.exposed_only.push(wordId);
                }
            }
        });

        // Process grammar mastery
        Object.entries(masteryData.grammar || {}).forEach(([grammarId, mastery]) => {
            if (mastery.isExposed) {
                const hasIncorrect = Object.values(mastery.learning || {}).some(attempt => !attempt.isCorrect);
                const hasCorrect = Object.values(mastery.learning || {}).some(attempt => attempt.isCorrect);

                if (hasIncorrect) {
                    profile.grammar.has_incorrect.push(grammarId);
                } else if (hasCorrect) {
                    profile.grammar.all_correct.push(grammarId);
                } else {
                    profile.grammar.exposed_only.push(grammarId);
                }
            }
        });

        setProfile(profile);
    }, [masteryData]);

    return { profile, loading, error };
};
